/**
 * ClockInModal - Modal for clocking in an operator/packer to a sub-session.
 *
 * Opened from the Daily Queue by clicking a sub-session row.
 * Flow: scan badge → lookup employee → select role → confirm clock-in.
 * Uses existing operator-service /api/operator/clockin/* endpoints.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { X, User, UserCheck, ScanLine, AlertTriangle, CheckCircle, Loader2, LogOut, Coffee, Camera } from 'lucide-react';
import CameraScannerModal from './CameraScannerModal';
import { useLanguage } from '../i18n/LanguageContext';
import { DailyWorkQueueItem } from '../services/productionService';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { useWorkerStatus } from '../hooks/useWorkerStatus';
import { isBadgeCode, normalizeBadgeCode, extractEmployeeNumber } from '../utils/barcodeUtils';
import api from '../services/api';
import operatorService from '../services/operatorService';
// wsService no longer needed here — useWorkerStatus handles WebSocket listeners
import toast from 'react-hot-toast';
import type { EmployeeInfo } from '../types';
import { getStatusBadgeClass } from '../utils/statusColors';

interface Assignment {
  user_id: string;
  employee_number: string;
  name: string;
  clocked_in_at: string;
  on_break?: boolean;
}

interface ExistingAssignment {
  sub_session_number: string;
  station_id: number;
  role: string;
  station_name: string;
}

type ModalStep = 'scan' | 'role' | 'confirm_relief' | 'confirm_transfer' | 'already_here' | 'success';

interface ClockInModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: DailyWorkQueueItem | null;
  onClockInSuccess: () => void;
}

const ClockInModal: React.FC<ClockInModalProps> = ({
  isOpen,
  onClose,
  item,
  onClockInSuccess,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const manualInputRef = useRef<HTMLInputElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Worker status — single source of truth via useWorkerStatus hook
  const workerStatus = useWorkerStatus(item?.station_id, item?.sub_session_number, isOpen);

  // State
  const [step, setStep] = useState<ModalStep>('scan');
  const [employee, setEmployee] = useState<EmployeeInfo | null>(null);
  const [existingAssignment, setExistingAssignment] = useState<ExistingAssignment | null>(null);
  const [selectedRole, setSelectedRole] = useState<'operator' | 'packer' | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [clockingIn, setClockingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transferConfirmed, setTransferConfirmed] = useState(false);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [successAction, setSuccessAction] = useState<'scanned_in' | 'scanned_out' | null>(null);
  const [successRole, setSuccessRole] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);

  // Reset state when modal opens/closes or item changes
  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (isOpen && item) {
      setStep('scan');
      setEmployee(null);
      setExistingAssignment(null);
      setSelectedRole(null);
      setError(null);
      setLookingUp(false);
      setClockingIn(false);
      setTransferConfirmed(false);
      setCurrentRole(null);
      setActionLoading(false);
      setSuccessAction(null);
      setSuccessRole(null);
    }
  }, [isOpen, item?.sub_session_number]);

  // Handle badge scan
  // If the scanned badge matches the pre-assigned operator or packer, auto-clock them in.
  // If it's a different employee, show role selection.
  const handleBadgeScan = useCallback(async (badgeCode: string) => {
    if (!item?.station_id) return;
    setError(null);
    setLookingUp(true);

    const normalized = isBadgeCode(badgeCode) ? normalizeBadgeCode(badgeCode) : `@+${badgeCode}`;
    const empNumber = extractEmployeeNumber(normalized);

    try {
      const response = await api.post('/operator/clockin/lookup-employee', {
        badge_code: normalized,
      });

      if (response.data.success) {
        const emp = response.data.employee;
        setEmployee(emp);
        setExistingAssignment(response.data.existing_assignment);

        // If employee is already at a different station, show transfer confirmation
        if (response.data.existing_assignment &&
            response.data.existing_assignment.sub_session_number !== item.sub_session_number) {
          setStep('confirm_transfer');
          return;
        }

        // If employee is already at THIS sub-session, show scan-out / take-break options
        if (response.data.existing_assignment &&
            response.data.existing_assignment.sub_session_number === item.sub_session_number) {
          const existingRole = response.data.existing_assignment.role;
          setCurrentRole(existingRole);
          workerStatus.refetch();
          setStep('already_here');
          return;
        }

        // Check if badge matches the pre-assigned operator or packer
        const isPreAssignedOperator = item.operator_employee_number && empNumber === item.operator_employee_number;
        const isPreAssignedPacker = item.packer_employee_number && empNumber === item.packer_employee_number;

        if (isPreAssignedOperator || isPreAssignedPacker) {
          // Auto-detect role and clock in immediately
          const autoRole = isPreAssignedOperator ? 'operator' : 'packer';
          setSelectedRole(autoRole);
          setStep('role');
          // Trigger auto-clock-in after state is set
          setTimeout(() => handleClockInDirect(emp, autoRole, normalized), 100);
        } else {
          // Different employee - show role selection
          setStep('role');
        }
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('clockInModal.error');
      setError(msg);
    } finally {
      setLookingUp(false);
    }
  }, [item, t]);

  // Barcode scanner hook
  const { isScanning } = useBarcodeScanner({
    onScan: handleBadgeScan,
    enabled: isOpen && step === 'scan' && !lookingUp,
    minLength: 3,
  });

  // Direct clock-in for pre-assigned employees (auto-detected role, skips role selection)
  const handleClockInDirect = async (_emp: EmployeeInfo, role: 'operator' | 'packer', badgeCode: string) => {
    if (!item?.station_id) return;

    setClockingIn(true);
    setError(null);

    try {
      const response = await api.post('/operator/clockin/clock-in', {
        station_id: item.station_id,
        badge_code: badgeCode,
        role,
        confirm_relief: false,
        auto_transfer: false,
      });

      if (response.data.success) {
        if (response.data.action === 'already_assigned') {
          setCurrentRole(response.data.current_role || role);
          setStep('already_here');
        } else {
          toast.success(t('clockInModal.success'));
          setSuccessAction('scanned_in');
          setSuccessRole(role);
          workerStatus.refetch();
          setStep('success');
          onClockInSuccess();
          closeTimerRef.current = setTimeout(() => onClose(), 5000);
        }
      } else if (response.data.action === 'needs_confirmation') {
        setSelectedRole(role);
        setStep('confirm_relief');
      } else if (response.data.action === 'needs_transfer') {
        setStep('confirm_transfer');
      }
    } catch (err: any) {
      const msg = getClockInErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setClockingIn(false);
    }
  };

  // Map backend error codes to i18n-translated messages
  const getClockInErrorMessage = (err: any): string => {
    const errorCode = err?.response?.data?.error;
    const subSessionNumber = err?.response?.data?.sub_session_number || '';
    switch (errorCode) {
      case 'session_completed':
        return t('clockInModal.errorSessionCompleted');
      case 'session_cancelled':
        return t('clockInModal.errorSessionCancelled');
      case 'no_active_session':
        return t('clockInModal.errorNoActiveSession');
      case 'materials_not_received':
        return t('clockInModal.materialsNotReceived').replace('{subSessionNumber}', subSessionNumber);
      case 'awaiting_assignment':
        return t('clockInModal.awaitingAssignment').replace('{subSessionNumber}', subSessionNumber);
      case 'scheduled_future':
        return t('clockInModal.scheduledFuture').replace('{subSessionNumber}', subSessionNumber);
      default:
        return err?.response?.data?.message || t('clockInModal.error');
    }
  };

  // Handle role selection and clock-in
  const handleClockIn = async (role: 'operator' | 'packer', confirmRelief = false, autoTransfer = false) => {
    if (!item?.station_id || !employee) return;

    const badgeCode = `@+${employee.employee_number}`;

    // Check if the role is occupied and we haven't confirmed relief yet
    const currentWorker = workerStatus[role as 'operator' | 'packer'];
    if (currentWorker && !confirmRelief) {
      setSelectedRole(role);
      setStep('confirm_relief');
      return;
    }

    setClockingIn(true);
    setError(null);

    try {
      const response = await api.post('/operator/clockin/clock-in', {
        station_id: item.station_id,
        badge_code: badgeCode,
        role,
        confirm_relief: confirmRelief,
        auto_transfer: autoTransfer || transferConfirmed,
      });

      if (response.data.success) {
        if (response.data.action === 'already_assigned') {
          setCurrentRole(response.data.current_role || role);
          setStep('already_here');
        } else {
          toast.success(t('clockInModal.success'));
          setSuccessAction('scanned_in');
          setSuccessRole(role);
          workerStatus.refetch();
          setStep('success');
          onClockInSuccess();
          closeTimerRef.current = setTimeout(() => onClose(), 5000);
        }
      } else if (response.data.action === 'needs_confirmation') {
        setSelectedRole(role);
        setStep('confirm_relief');
      } else if (response.data.action === 'needs_transfer') {
        setStep('confirm_transfer');
      }
    } catch (err: any) {
      const msg = getClockInErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setClockingIn(false);
    }
  };

  // Handle scan out - clocks the employee out of the current station
  const handleScanOut = async () => {
    if (!employee || !item?.station_id) return;
    setActionLoading(true);
    setError(null);
    try {
      const response = await api.post('/operator/clockin/clock-out', {
        badge_code: `@+${employee.employee_number}`,
        station_id: item.station_id,
        reason: 'scan_out',
      });
      if (response.data.success) {
        toast.success(t('clockInModal.scanOutSuccess'));
        setSuccessAction('scanned_out');
        setSuccessRole(currentRole);
        workerStatus.refetch();
        setStep('success');
        onClockInSuccess();
        closeTimerRef.current = setTimeout(() => onClose(), 5000);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('clockInModal.error');
      setError(msg);
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle take break — uses centralized workerStatus.takeBreak
  const handleTakeBreak = async () => {
    if (!employee || !item?.sub_session_number) return;
    setActionLoading(true);
    setError(null);
    try {
      await workerStatus.takeBreak(
        employee.employee_number,
        employee.display_name || employee.employee_number,
        (currentRole as 'operator' | 'packer') || 'operator'
      );
      toast.success(t('clockInModal.breakStarted') || 'Break started');
      setSuccessAction('scanned_out');
      setSuccessRole(currentRole);
      setStep('success');
      onClockInSuccess();
      closeTimerRef.current = setTimeout(() => onClose(), 5000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('clockInModal.error');
      setError(msg);
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle manual entry submit
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = manualInputRef.current?.value?.trim();
    if (input) {
      handleBadgeScan(input);
      if (manualInputRef.current) manualInputRef.current.value = '';
    }
  };

  if (!isOpen || !item) return null;

  const canClockIn = item.station_id &&
    ['ready', 'active', 'paused'].includes(item.status);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('clockInModal.title')}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Sub-session info header */}
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(workerStatus.sessionStatus || item.status)}`}>
                {t(`status.${workerStatus.sessionStatus || item.status}`) || workerStatus.sessionStatus || item.status}
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.product_name}</span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{item.station_name || '—'}</span>
              <span className="font-mono">{item.sub_session_number}</span>
              <span>{item.expected_boxes} {t('dailyWorkQueue.expectedBoxes').toLowerCase()}</span>
            </div>
          </div>

          {/* Current assignments */}
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              {t('clockInModal.currentAssignments')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Operator */}
              {(() => {
                const clockedIn = workerStatus.operator;
                const onBreak = clockedIn?.on_break || (!clockedIn && workerStatus.operator?.on_break);
                const preAssigned = !clockedIn && item.operator_name && !onBreak;
                return (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                    onBreak
                      ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                      : clockedIn
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : preAssigned
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                          : 'bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700'
                  }`}>
                    {onBreak
                      ? <Coffee className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      : <User className={`w-4 h-4 ${clockedIn ? 'text-green-600 dark:text-green-400' : preAssigned ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                    }
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('clockInModal.operator')}</div>
                      {workerStatus.isLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                      ) : onBreak ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{clockedIn?.name || clockedIn?.employee_number || item.operator_name}</div>
                          <div className="text-xs text-amber-600 dark:text-amber-400">{t('clockInModal.onBreak')}</div>
                        </div>
                      ) : clockedIn ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{clockedIn.name || clockedIn.employee_number}</div>
                          <div className="text-xs text-green-600 dark:text-green-400">{t('clockInModal.since')} {formatTime(clockedIn.clocked_in_at)}</div>
                        </div>
                      ) : preAssigned ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.operator_name}</div>
                          <div className="text-xs text-blue-500 dark:text-blue-400">Assigned (not scanned in)</div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 italic">{t('clockInModal.notAssigned')}</div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Packer */}
              {(() => {
                const clockedIn = workerStatus.packer;
                const onBreak = clockedIn?.on_break || (!clockedIn && workerStatus.packer?.on_break);
                const preAssigned = !clockedIn && item.packer_name && !onBreak;
                return (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                    onBreak
                      ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                      : clockedIn
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : preAssigned
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                          : 'bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700'
                  }`}>
                    {onBreak
                      ? <Coffee className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      : <User className={`w-4 h-4 ${clockedIn ? 'text-green-600 dark:text-green-400' : preAssigned ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                    }
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('clockInModal.packer')}</div>
                      {workerStatus.isLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                      ) : onBreak ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{clockedIn?.name || clockedIn?.employee_number || item.packer_name}</div>
                          <div className="text-xs text-amber-600 dark:text-amber-400">{t('clockInModal.onBreak')}</div>
                        </div>
                      ) : clockedIn ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{clockedIn.name || clockedIn.employee_number}</div>
                          <div className="text-xs text-green-600 dark:text-green-400">{t('clockInModal.since')} {formatTime(clockedIn.clocked_in_at)}</div>
                        </div>
                      ) : preAssigned ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{item.packer_name}</div>
                          <div className="text-xs text-blue-500 dark:text-blue-400">Assigned (not scanned in)</div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 italic">{t('clockInModal.notAssigned')}</div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Main content area */}
          <div className="px-6 py-5">
            {!canClockIn ? (
              /* No station or bad status */
              <div className="text-center py-6">
                <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {!item.station_id
                    ? t('clockInModal.noStation')
                    : item.status === 'pending'
                      ? t('clockInModal.materialsNotReceived').replace('{subSessionNumber}', item.sub_session_number || '')
                      : item.status === 'received'
                        ? (item.scheduled_date && new Date(item.scheduled_date + 'T00:00:00') > new Date(new Date().toDateString())
                          ? t('clockInModal.scheduledFuture').replace('{subSessionNumber}', item.sub_session_number || '')
                          : t('clockInModal.awaitingAssignment').replace('{subSessionNumber}', item.sub_session_number || ''))
                        : t('clockInModal.sessionNotActive').replace('{status}', t(`status.${item.status}`) || item.status)
                  }
                </p>
              </div>
            ) : step === 'scan' ? (
              /* Step 1: Scan badge */
              <div className="space-y-4">
                <div className="text-center py-4">
                  <ScanLine className={`w-12 h-12 mx-auto mb-3 hidden sm:block ${isScanning ? 'text-blue-500 animate-pulse' : 'text-gray-400'}`} />
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {t('clockInModal.scanBadge')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 hidden sm:block">
                    {t('clockInModal.scanBadgeHint')}
                  </p>
                </div>

                {lookingUp && (
                  <div className="flex items-center justify-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('clockInModal.lookingUp')}
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                )}

                {/* Manual entry */}
                <form onSubmit={handleManualSubmit} className="flex items-center gap-2">
                  <input
                    ref={manualInputRef}
                    type="text"
                    inputMode="none"
                    onTouchStart={(e) => (e.currentTarget.inputMode = 'text')}
                    placeholder={t('clockInModal.badgePlaceholder')}
                    className="flex-1 min-w-0 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={lookingUp}
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={lookingUp}
                    className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {t('common.go')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCamera(true)}
                    className="flex-shrink-0 px-2.5 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title={t('cameraScanner.openCamera') || 'Use Camera'}
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                </form>
              </div>
            ) : step === 'role' ? (
              /* Step 2: Select role */
              <div className="space-y-4">
                {/* Employee info */}
                {employee && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <UserCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{employee.display_name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">#{employee.employee_number}</div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                )}

                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('clockInModal.selectRole')}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleClockIn('operator')}
                    disabled={clockingIn}
                    className="flex flex-col items-center gap-2 px-4 py-5 rounded-xl border-2 border-blue-200 dark:border-blue-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all disabled:opacity-50"
                  >
                    <User className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('clockInModal.operator')}</span>
                    {workerStatus.operator && (
                      <span className="text-xs text-orange-600 dark:text-orange-400">
                        ({workerStatus.operator.name || workerStatus.operator.employee_number})
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => handleClockIn('packer')}
                    disabled={clockingIn}
                    className="flex flex-col items-center gap-2 px-4 py-5 rounded-xl border-2 border-green-200 dark:border-green-700 hover:border-green-500 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/30 transition-all disabled:opacity-50"
                  >
                    <User className="w-8 h-8 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('clockInModal.packer')}</span>
                    {workerStatus.packer && (
                      <span className="text-xs text-orange-600 dark:text-orange-400">
                        ({workerStatus.packer.name || workerStatus.packer.employee_number})
                      </span>
                    )}
                  </button>
                </div>

                {clockingIn && (
                  <div className="flex items-center justify-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('clockInModal.clockingIn')}
                  </div>
                )}

                <button
                  onClick={() => {
                    setEmployee(null);
                    setStep('scan');
                    setError(null);
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  &larr; {t('common.back')}
                </button>
              </div>
            ) : step === 'confirm_relief' ? (
              /* Step 3a: Confirm relief */
              <div className="space-y-4">
                <div className="text-center py-2">
                  <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {t('clockInModal.relieveConfirm').replace('{name}',
                      (selectedRole && workerStatus[selectedRole as 'operator' | 'packer']?.name) || workerStatus[selectedRole as 'operator' | 'packer']?.employee_number || ''
                    )}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t('clockInModal.relieveDescription')
                      .replace('{name}', (selectedRole && workerStatus[selectedRole as 'operator' | 'packer']?.name) || workerStatus[selectedRole as 'operator' | 'packer']?.employee_number || '')
                      .replace('{role}', selectedRole === 'operator' ? t('clockInModal.operator') : t('clockInModal.packer'))
                    }
                  </p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setStep('role');
                      setError(null);
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => selectedRole && handleClockIn(selectedRole, true)}
                    disabled={clockingIn}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {clockingIn ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('clockInModal.clockingIn')}
                      </span>
                    ) : t('clockInModal.yesRelieve')}
                  </button>
                </div>
              </div>
            ) : step === 'confirm_transfer' ? (
              /* Step 3b: Confirm transfer from another station */
              <div className="space-y-4">
                <div className="text-center py-2">
                  <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {t('clockInModal.transferConfirm').replace('{station}', existingAssignment?.station_name || '')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t('clockInModal.transferDescription')
                      .replace('{station}', existingAssignment?.station_name || '')
                      .replace('{role}', existingAssignment?.role === 'operator' ? t('clockInModal.operator') : t('clockInModal.packer'))
                    }
                  </p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setStep('scan');
                      setEmployee(null);
                      setExistingAssignment(null);
                      setTransferConfirmed(false);
                      setError(null);
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      setTransferConfirmed(true);
                      setStep('role');
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    {t('clockInModal.yesTransfer')}
                  </button>
                </div>
              </div>
            ) : step === 'already_here' ? (
              /* Already scanned in - show scan out / take break options */
              <div className="space-y-4">
                {/* Employee info */}
                {employee && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{employee.display_name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t('clockInModal.currentlyAs')} <span className="capitalize font-medium">{currentRole === 'operator' ? t('clockInModal.operator') : t('clockInModal.packer')}</span>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                )}

                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                  {t('clockInModal.alreadyHerePrompt')}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleScanOut}
                    disabled={actionLoading}
                    className="flex flex-col items-center gap-2 px-4 py-5 rounded-xl border-2 border-red-200 dark:border-red-700 hover:border-red-500 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all disabled:opacity-50"
                  >
                    <LogOut className="w-8 h-8 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('clockInModal.scanOut')}</span>
                  </button>
                  <button
                    onClick={handleTakeBreak}
                    disabled={actionLoading}
                    className="flex flex-col items-center gap-2 px-4 py-5 rounded-xl border-2 border-yellow-200 dark:border-yellow-700 hover:border-yellow-500 dark:hover:border-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 transition-all disabled:opacity-50"
                  >
                    <Coffee className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('clockInModal.takeBreak')}</span>
                  </button>
                </div>

                {actionLoading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                )}
              </div>
            ) : step === 'success' ? (
              /* Success */
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                  {successAction === 'scanned_out' ? t('clockInModal.scanOutSuccess') : t('clockInModal.success')}
                </h3>
                {employee && (
                  <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium">{employee.display_name}</span>
                    {successRole && (
                      <span className="text-gray-400 dark:text-gray-500"> — {successRole === 'operator' ? t('clockInModal.operator') : t('clockInModal.packer')}</span>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>

      {/* Camera Scanner Modal — backup for USB barcode scanner */}
      <CameraScannerModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onScan={(barcode) => {
          setShowCamera(false);
          handleBadgeScan(barcode);
        }}
      />
    </div>
  );
};

export default ClockInModal;
